"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  ComponentsToDirection,
  PolarToComponents,
} from "@/components/tools/linear-algebra/vector-direction";
import {
  TwoVectorResults,
  type VectorCtx,
} from "@/components/tools/linear-algebra/vector-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type AngleUnit,
  parseAngle,
} from "@/lib/tool-kit/linear-algebra/angle-input";
import type { Vec } from "@/lib/tool-kit/linear-algebra/vector";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  type NumberStyle,
  parseRational,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const VECTOR_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modes: {
    two: "Work with two vectors",
    toDirection: "Components → magnitude and direction",
    fromDirection: "Magnitude and direction → components",
  },
  dimensionLabel: "Dimension",
  dimensions: { "2": "2D (x, y)", "3": "3D (x, y, z)" },
  unitLabel: "Angle unit",
  units: { degrees: "Degrees", radians: "Radians" },
  vectorU: "Vector u",
  vectorV: "Vector v",
  vectorOnly: "Vector v",
  component: "{name}, {axis} component",
  scalarLabel: "Scalar k",
  magnitudeInput: "Magnitude r",
  angleInput: "Direction angle θ (from the positive x-axis)",
  angleHintDegrees: "Degrees, such as 30 or −45.",
  angleHintRadians:
    "Radians: a number such as 0.5236, or a multiple of π such as pi/6 or 2pi/3.",
  entryHint: "Whole numbers, decimals or fractions such as 3/4.",
  results: "Results",
  enterAll: "Fill in every component to see the results.",
  invalid:
    "Enter each value as a number or a fraction, such as 2, −0.5 or 3/4.",
  invalidAngle:
    "Enter the angle as a number; in radians you can also write pi/6 or 2pi/3.",
  zeroVector: "Undefined for the zero vector",
  copied: "Copied!",
  copyResults: "Copy results",
  dotLabel: "Dot product u · v",
  rowLabels: {
    add: "Sum u + v",
    subtract: "Difference u − v",
    scaleU: "Scalar multiple {k} × u",
    scaleV: "Scalar multiple {k} × v",
    magnitude: "Magnitude |{name}|",
    unit: "Unit vector in the direction of {name}",
    dot: "Dot product u · v",
    cross: "Cross product u × v",
    cross2D: "Cross product u × v (the z-component)",
    area: "Area of the parallelogram on u and v",
    angle: "Angle between u and v",
    projection: "Projection of {from} onto {onto}",
    scalarProjection: "Scalar projection of {from} along {onto}",
  },
  unitWorking: "{name} ÷ |{name}|: divide each component by the magnitude",
  areaWorking:
    "|u × v| = √(|u|²|v|² − (u · v)²); the triangle on u and v has half this area",
  angleWorking:
    "cos θ = (u · v) / (|u| |v|) = {dot} / ({magU} × {magV}) = {cos}",
  angleZero: "The angle is undefined when either vector is the zero vector.",
  projectionWorking:
    "proj = (({from} · {onto}) / ({onto} · {onto})) × {onto}, with factor {factor}",
  scalarWorking:
    "comp = (u · v) / |v|, the signed length of the shadow of {from} along {onto}",
  degreesUnit: "{value}°",
  radiansUnit: "{value} rad",
  orthogonal:
    "The dot product is 0, so u and v are perpendicular (orthogonal).",
  parallel:
    "The cross product is zero, so u and v are parallel (one is a multiple of the other).",
  plotHeading: "Diagram",
  plotTitle:
    "The vectors u and v drawn from the origin to scale, with u + v as the diagonal of the parallelogram and the foot of the projection of u onto v",
  plotTitleOne: "The vector v drawn from the origin to scale",
  plotNote:
    "Dashed lines complete the parallelogram; the dashed segment from the tip of u ends at proj, the projection of u onto v.",
  noPlot3d:
    "Three-dimensional vectors are not drawn; the exact results above are complete.",
  downloadPng: "Download diagram (PNG)",
  magnitudeLabel: "Magnitude |v|",
  directionLabel: "Direction θ",
  quadrantLabel: "Location",
  onAxis: "On an axis",
  quadrantN: "Quadrant {n}",
  quadrants: { "1": "I", "2": "II", "3": "III", "4": "IV" },
  magnitudeWorking: "|v| = √(x² + y²) = √({x}² + {y}²) = √{sum} = {magnitude}",
  directionWorking:
    "θ = atan2(y, x) = atan2({y}, {x}) = {angle}, taken in [0°, 360°)",
  referenceWorking:
    "The reference angle is arctan(|y/x|) ≈ {ref}°, and the vector lies in quadrant {quadrant}, which fixes the angle.",
  angleWithAxis: "Angle with the {axis}-axis",
  cosinesWorking: "Direction cosines (v_x, v_y, v_z) / |v| = ({cosines})",
  cosinesCheck: "The squares of the direction cosines always add up to 1.",
  xComponent: "x = r cos θ",
  yComponent: "y = r sin θ",
  componentWorking: "r = {r}, θ = {angle}",
  componentX: "x = r cos θ = {x}",
  componentY: "y = r sin θ = {y}",
  decimalNote: "Values are rounded to 6 decimal places.",
} as const;

export type VectorStrings = typeof VECTOR_CALCULATOR_STRINGS;

type Mode = keyof VectorStrings["modes"];
type Dimension = keyof VectorStrings["dimensions"];

const DECIMAL_INPUT = /[.,]/;
const AXES = ["x", "y", "z"] as const;
const MODE_ORDER: Mode[] = ["two", "toDirection", "fromDirection"];

function parseVector(texts: string[]): Vec | "empty" | "invalid" {
  if (texts.some((text) => text.trim() === "")) {
    return "empty";
  }
  const parsed = texts.map(parseRational);
  return parsed.some((x) => x === null) ? "invalid" : (parsed as Rational[]);
}

function VectorInputs({
  name,
  legend,
  dimension,
  values,
  onChange,
  strings,
  idPrefix,
}: {
  name: string;
  legend: string;
  dimension: number;
  values: string[];
  onChange: (index: number, value: string) => void;
  strings: VectorStrings;
  idPrefix: string;
}) {
  return (
    <fieldset className="space-y-3 rounded-lg border border-border p-4">
      <legend className="px-1 font-medium text-foreground text-sm">
        {legend}
      </legend>
      <div className="grid grid-cols-3 gap-3">
        {AXES.slice(0, dimension).map((axis, i) => (
          <TextField
            id={`${idPrefix}-${axis}`}
            key={axis}
            label={t(strings.component, { name, axis })}
            mono
            numeric
            onChange={(value) => onChange(i, value)}
            value={values[i]}
          />
        ))}
      </div>
    </fieldset>
  );
}

export default function VectorCalculatorWidget() {
  const strings = useToolUi(VECTOR_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("two");
  const [dimension, setDimension] = useState<Dimension>("3");
  const [unit, setUnit] = useState<AngleUnit>("degrees");
  const [u, setU] = useState(["3", "4", "0"]);
  const [v, setV] = useState(["1", "−2", "2"]);
  const [k, setK] = useState("2");
  const [r, setR] = useState("10");
  const [theta, setTheta] = useState("30");
  const n = mode === "fromDirection" ? 2 : Number(dimension);
  const update = (set: typeof setU) => (index: number, value: string) =>
    set((prev) => prev.map((x, i) => (i === index ? value : x)));
  const texts = [
    ...u.slice(0, n),
    ...(mode === "two" ? [...v.slice(0, n), k] : []),
    r,
  ];
  const style: NumberStyle = texts.some((text) => DECIMAL_INPUT.test(text))
    ? "decimal"
    : "fraction";
  const ctx: VectorCtx = {
    strings,
    style,
    mark: decimalMarkOf(fmt),
    unit,
    fmt,
  };
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="vc-mode"
        onChange={setMode}
        options={MODE_ORDER.map((id) => ({
          value: id,
          label: strings.modes[id],
        }))}
        value={mode}
      />
      <div className="flex flex-wrap gap-6">
        {mode === "fromDirection" ? null : (
          <PillGroup
            label={strings.dimensionLabel}
            name="vc-dim"
            onChange={setDimension}
            options={(["2", "3"] as const).map((id) => ({
              value: id,
              label: strings.dimensions[id],
            }))}
            value={dimension}
          />
        )}
        {mode === "two" ? null : (
          <PillGroup
            label={strings.unitLabel}
            name="vc-unit"
            onChange={setUnit}
            options={(["degrees", "radians"] as const).map((id) => ({
              value: id,
              label: strings.units[id],
            }))}
            value={unit}
          />
        )}
      </div>
      <Inputs
        ctx={ctx}
        mode={mode}
        n={n}
        onChange={{
          u: update(setU),
          v: update(setV),
          k: setK,
          r: setR,
          theta: setTheta,
        }}
        values={{ u, v, k, r, theta }}
      />
      <p className="text-muted-foreground text-xs">{strings.entryHint}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome ctx={ctx} mode={mode} n={n} values={{ u, v, k, r, theta }} />
      </section>
    </>
  );
}

interface Values {
  u: string[];
  v: string[];
  k: string;
  r: string;
  theta: string;
}

function Inputs({
  ctx,
  mode,
  n,
  values,
  onChange,
}: {
  ctx: VectorCtx;
  mode: Mode;
  n: number;
  values: Values;
  onChange: {
    u: (i: number, value: string) => void;
    v: (i: number, value: string) => void;
    k: (value: string) => void;
    r: (value: string) => void;
    theta: (value: string) => void;
  };
}) {
  const { strings } = ctx;
  if (mode === "fromDirection") {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="vc-r"
          label={strings.magnitudeInput}
          mono
          numeric
          onChange={onChange.r}
          value={values.r}
        />
        <TextField
          hint={
            ctx.unit === "degrees"
              ? strings.angleHintDegrees
              : strings.angleHintRadians
          }
          id="vc-theta"
          label={strings.angleInput}
          mono
          onChange={onChange.theta}
          value={values.theta}
        />
      </div>
    );
  }
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <VectorInputs
        dimension={n}
        idPrefix="vc-u"
        legend={mode === "two" ? strings.vectorU : strings.vectorOnly}
        name={mode === "two" ? "u" : "v"}
        onChange={onChange.u}
        strings={strings}
        values={values.u}
      />
      {mode === "two" ? (
        <VectorInputs
          dimension={n}
          idPrefix="vc-v"
          legend={strings.vectorV}
          name="v"
          onChange={onChange.v}
          strings={strings}
          values={values.v}
        />
      ) : null}
      {mode === "two" ? (
        <TextField
          id="vc-k"
          label={strings.scalarLabel}
          mono
          numeric
          onChange={onChange.k}
          value={values.k}
        />
      ) : null}
    </div>
  );
}

function Outcome({
  ctx,
  mode,
  n,
  values,
}: {
  ctx: VectorCtx;
  mode: Mode;
  n: number;
  values: Values;
}) {
  const { strings } = ctx;
  if (mode === "fromDirection") {
    const r = parseRational(values.r);
    const angle = parseAngle(values.theta, ctx.unit);
    if (values.r.trim() === "" || values.theta.trim() === "") {
      return <Notice>{strings.enterAll}</Notice>;
    }
    if (r === null) {
      return <ErrorNotice>{strings.invalid}</ErrorNotice>;
    }
    if (angle === null) {
      return <ErrorNotice>{strings.invalidAngle}</ErrorNotice>;
    }
    return <PolarToComponents angle={angle} ctx={ctx} r={r} />;
  }
  const first = parseVector(values.u.slice(0, n));
  const second = mode === "two" ? parseVector(values.v.slice(0, n)) : [];
  const k = mode === "two" ? parseRational(values.k) : null;
  const parts = [first, second];
  if (
    parts.some((p) => p === "empty") ||
    (mode === "two" && values.k.trim() === "")
  ) {
    return <Notice>{strings.enterAll}</Notice>;
  }
  if (parts.some((p) => p === "invalid") || (mode === "two" && k === null)) {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  if (mode === "two" && k) {
    return (
      <TwoVectorResults ctx={ctx} k={k} u={first as Vec} v={second as Vec} />
    );
  }
  return <ComponentsToDirection ctx={ctx} vec={first as Vec} />;
}
